import { useSyncExternalStore } from 'react';
import { registerSW } from 'virtual:pwa-register';
import { enqueueOutbox, isNetworkFailure, listOutbox, newOutboxId, removeOutbox, replayOutbox, requeueFailedOutbox, requeueOutbox, type OutboxEntry, type OutboxKind, type ReplayResult } from './outbox';
import { shouldReloadOnTakeover, watchInteraction } from './takeover';

/**
 * The page's side of the offline story (planning-app.md §4.4): the service worker registration,
 * the online/offline state, the "as of" stamps the worker puts on cached answers, the outbox the
 * comment and consent forms fall back to, and the Web Push subscription of this browser.
 */

/* ---------- Small external stores ---------- */

function store<T>(initial: T) {
  let value = initial;
  const listeners = new Set<() => void>();
  return {
    get: () => value,
    set(next: T) {
      value = next;
      for (const l of listeners) l();
    },
    subscribe(l: () => void) {
      listeners.add(l);
      return () => {
        listeners.delete(l);
      };
    },
  };
}

const online = store<boolean>(typeof navigator === 'undefined' ? true : navigator.onLine);
/** Paths answered from the worker's cache → when that answer was fetched. Cleared on a fresh network answer. */
const freshness = store<ReadonlyMap<string, string>>(new Map());
const outbox = store<OutboxEntry[]>([]);
const lastReplay = store<ReplayResult[]>([]);

export function useOnline(): boolean {
  return useSyncExternalStore(online.subscribe, online.get, () => true);
}

/** The "as of" stamp of a path served from the offline cache, or null when the last answer was live. */
export function useAsOf(path: string): string | null {
  return useSyncExternalStore(freshness.subscribe, () => freshness.get().get(path) ?? null, () => null);
}

/** The oldest "as of" of anything on screen that came from the cache, for the banner. */
export function useAnyAsOf(): string | null {
  return useSyncExternalStore(
    freshness.subscribe,
    () => {
      let oldest: string | null = null;
      for (const v of freshness.get().values()) if (!oldest || v < oldest) oldest = v;
      return oldest;
    },
    () => null,
  );
}

export function useOutbox(): OutboxEntry[] {
  return useSyncExternalStore(outbox.subscribe, outbox.get, () => []);
}

export function useLastReplay(): ReplayResult[] {
  return useSyncExternalStore(lastReplay.subscribe, lastReplay.get, () => []);
}

export function clearLastReplay(): void {
  lastReplay.set([]);
}

/** Called by the API client for every answer: records or clears the stamp for that path. */
export function recordFreshness(path: string, cachedAt: string | null): void {
  const current = freshness.get();
  if (cachedAt === null ? !current.has(path) : current.get(path) === cachedAt) return;
  const next = new Map(current);
  if (cachedAt === null) next.delete(path);
  else next.set(path, cachedAt);
  freshness.set(next);
}

async function refreshOutbox(): Promise<void> {
  try {
    outbox.set(await listOutbox());
  } catch {
    outbox.set([]);
  }
}

/* ---------- Service worker ---------- */

const supportsSw = typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
let swReady: Promise<ServiceWorkerRegistration | null> = Promise.resolve(null);
const replayListeners = new Set<(results: ReplayResult[]) => void>();

export function onOutboxReplayed(l: (results: ReplayResult[]) => void): () => void {
  replayListeners.add(l);
  return () => {
    replayListeners.delete(l);
  };
}

function announceReplay(results: ReplayResult[]): void {
  if (results.length === 0) return;
  lastReplay.set(results);
  for (const l of replayListeners) l(results);
  void refreshOutbox();
}

/** Registers the worker (production builds only) and wires the online/offline and outbox plumbing. */
export function startOffline(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('online', () => {
    online.set(true);
    void replayNow();
  });
  window.addEventListener('offline', () => online.set(false));
  void refreshOutbox();

  if (!supportsSw || !import.meta.env.PROD) return;
  // A new worker taking over a young, untouched page: start again on the new shell rather than run
  // the previous build against the new API (takeover.ts has the rule and the reason).
  const hadController = navigator.serviceWorker.controller !== null;
  const interaction = watchInteraction(window);
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (shouldReloadOnTakeover({ hadController, ageMs: performance.now(), interacted: interaction.touched() })) window.location.reload();
  });
  swReady = new Promise((resolve) => {
    registerSW({
      immediate: true,
      onRegisteredSW(_url, registration) {
        resolve(registration ?? null);
      },
      onRegisterError() {
        resolve(null);
      },
    });
  });
  navigator.serviceWorker.addEventListener('message', (event: MessageEvent<{ type?: string; results?: ReplayResult[] }>) => {
    if (event.data?.type === 'outbox-replayed' && event.data.results) announceReplay(event.data.results);
  });
}

function postToWorker(message: { type: 'purge' | 'replay' }): void {
  if (!supportsSw) return;
  navigator.serviceWorker.controller?.postMessage(message);
}

/** Sign-out: nothing read while signed in stays on the device. */
export async function purgeOffline(): Promise<void> {
  postToWorker({ type: 'purge' });
  try {
    const { clearOutbox } = await import('./outbox');
    await clearOutbox();
  } catch {
    /* no IndexedDB (private mode): nothing to clear */
  }
  freshness.set(new Map());
  outbox.set([]);
  // The worker's cache is deleted asynchronously; drop it from here too so the next sign-in starts clean.
  if (typeof caches !== 'undefined') await caches.delete('planning-api-v1').catch(() => undefined);
}

/** A failed entry back in the queue, replayed at once; or dropped for good, on the person's word. */
export async function retryOutboxEntry(id: string): Promise<ReplayResult[]> {
  await requeueOutbox(id);
  await refreshOutbox();
  return replayNow();
}

/** The banner's Retry: failed entries included, under their original keys. */
export async function retryAllOutbox(): Promise<ReplayResult[]> {
  await requeueFailedOutbox();
  await refreshOutbox();
  return replayNow();
}

export async function discardOutboxEntry(id: string): Promise<void> {
  await removeOutbox(id);
  await refreshOutbox();
}

/** Replays the outbox from the page; results are announced to whoever listens. */
export async function replayNow(): Promise<ReplayResult[]> {
  if (outbox.get().length === 0) await refreshOutbox();
  if (outbox.get().length === 0) return [];
  const results = await replayOutbox(fetch).catch(() => [] as ReplayResult[]);
  announceReplay(results);
  return results;
}

/**
 * Runs a write; when the network is the reason it failed, queues it instead and asks for a
 * Background Sync. The Idempotency-Key is minted before the first attempt and sent on it, so a
 * request whose answer was lost on the way back is replayed under the same identity and lands
 * once. Returns what happened so the form can say "sent" or "queued".
 */
export async function sendOrQueue<T>(
  run: (idempotencyKey: string) => Promise<T>,
  entry: { kind: OutboxKind; method: 'POST' | 'PUT'; path: string; body: unknown; subjectId: string; preview: string },
): Promise<{ outcome: 'sent'; value: T } | { outcome: 'queued'; entry: OutboxEntry }> {
  const id = newOutboxId();
  if (online.get()) {
    try {
      return { outcome: 'sent', value: await run(id) };
    } catch (err) {
      if (!isNetworkFailure(err)) throw err;
    }
  }
  const queued = await enqueueOutbox({ ...entry, id, csrf: csrfToken() });
  await refreshOutbox();
  const reg = await swReady;
  const sync = (reg as (ServiceWorkerRegistration & { sync?: { register(tag: string): Promise<void> } }) | null)?.sync;
  if (sync) await sync.register('planning-outbox').catch(() => undefined);
  return { outcome: 'queued', entry: queued };
}

function csrfToken(): string {
  const match = document.cookie.split('; ').find((c) => c.startsWith('brain_csrf='));
  return match ? decodeURIComponent(match.slice('brain_csrf='.length)) : '';
}

/* ---------- Web Push ---------- */

export type PushSupport = 'supported' | 'unsupported' | 'needs-install';

/** iOS Safari only exposes push to installed (Home Screen) apps; everywhere else it is a feature check. */
export function pushSupport(): PushSupport {
  if (typeof window === 'undefined' || !supportsSw || !('PushManager' in window) || !('Notification' in window)) {
    const ios = typeof navigator !== 'undefined' && /iphone|ipad|ipod/i.test(navigator.userAgent) && !isStandalone();
    return ios ? 'needs-install' : 'unsupported';
  }
  return 'supported';
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export async function currentPushEndpoint(): Promise<string | null> {
  const reg = await swReady;
  if (!reg) return null;
  const sub = await reg.pushManager.getSubscription();
  return sub?.endpoint ?? null;
}

export async function subscribeToPush(publicKey: string): Promise<{ endpoint: string; keys: { p256dh: string; auth: string }; userAgent: string }> {
  const reg = await swReady;
  if (!reg) throw new Error('service worker not registered');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new PushPermissionDenied();
  const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) }));
  const json = sub.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) throw new Error('push subscription incomplete');
  return { endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth }, userAgent: navigator.userAgent.slice(0, 400) };
}

export async function unsubscribeFromPush(): Promise<string | null> {
  const reg = await swReady;
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return null;
  await sub.unsubscribe();
  return sub.endpoint;
}

export class PushPermissionDenied extends Error {
  constructor() {
    super('notification permission denied');
    this.name = 'PushPermissionDenied';
  }
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}
